/** SessionStorage key that keeps the app the user came from across the sign-in flow. */
export const HUB_STORAGE_KEY = 'logto:aims-hub';

/**
 * The app that started sign-in, as far as the browser tells us: the origin of a cross-origin
 * `document.referrer` (the page that sent the user to `/oidc/auth`). Only the origin is kept, so
 * the link goes back to the app's front door, never to a callback or deep path.
 */
export const resolveHubUrl = (referrer: string, currentOrigin: string): string | undefined => {
  try {
    const url = new URL(referrer);

    if (!['http:', 'https:'].includes(url.protocol) || url.origin === currentOrigin) {
      return;
    }

    return `${url.origin}/`;
  } catch {
    return undefined;
  }
};

const readStorage = (): string | undefined => {
  try {
    return sessionStorage.getItem(HUB_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (value: string) => {
  try {
    sessionStorage.setItem(HUB_STORAGE_KEY, value);
  } catch {
    // Storage unavailable (privacy mode): the link shows only while the referrer is known.
  }
};

/**
 * The first app seen in this tab wins: after a social or SSO redirect the referrer is the
 * identity provider, which must not replace the app the user actually came from.
 */
export const readHubUrl = (referrer: string, currentOrigin: string): string | undefined => {
  const stored = readStorage();

  if (stored) {
    return stored;
  }

  const hubUrl = resolveHubUrl(referrer, currentOrigin);

  if (hubUrl) {
    writeStorage(hubUrl);
  }

  return hubUrl;
};
